import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Camera, Cog, Fuel, Gauge } from "lucide-react";
import { cn } from "@/lib/utils";
import { badgesFor, gbp, miles, vehiclePath } from "@/lib/inventory";
import { VehicleImage } from "@/components/vehicles/VehicleImage";

const Spec = ({ icon: Icon, label, value }) => (
  <div className="flex min-w-0 items-center gap-2">
    <Icon className="h-4 w-4 shrink-0 text-steel" aria-hidden="true" />
    <span className="sr-only">{label}: </span>
    <span className="text-sm leading-snug text-ink">{value}</span>
  </div>
);

export const Badges = ({ v, className }) => {
  const badges = badgesFor(v);
  if (!badges.length) return null;
  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {badges.map((b) => (
        <span key={b} className="rounded-sm bg-white/95 px-2 py-1 text-xs font-bold text-ink shadow-sm">
          {b}
        </span>
      ))}
    </div>
  );
};

export const VehicleCard = ({ v, testId, tag }) => {
  const path = vehiclePath(v);
  return (
    <article
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-line bg-white transition-[border-color,box-shadow] duration-300 hover:border-[#B9BFC9] hover:shadow-[0_24px_48px_-30px_rgba(11,13,18,0.55)]"
      data-testid={testId}
    >
      <Link to={path} tabIndex={-1} aria-hidden="true" className="relative block aspect-[4/3] overflow-hidden bg-paper">
        <div className="h-full w-full transition-transform duration-700 ease-out group-hover:scale-[1.04]">
          <VehicleImage v={v} />
        </div>
        <Badges v={v} className="absolute left-3 top-3" />
        {tag && (
          <span className="absolute right-3 top-3 rounded-sm bg-brand px-2 py-1 text-xs font-bold uppercase tracking-wider text-white">{tag}</span>
        )}
        {v.images.length > 0 && (
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-sm bg-ink/85 px-2 py-1 text-xs font-semibold text-white">
            <Camera className="h-3.5 w-3.5" aria-hidden="true" />
            {v.images.length}
          </span>
        )}
      </Link>
      <div className="flex flex-1 flex-col gap-4 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-extrabold leading-tight text-ink">
              <Link to={path} className="transition-colors hover:text-brand" data-testid={testId && `${testId}-title-link`}>
                {v.make} {v.model}
              </Link>
            </h3>
            <p className="mt-1 line-clamp-2 text-sm text-steel">{v.variant}</p>
          </div>
          <p className="shrink-0 font-display text-xl font-extrabold text-ink" data-testid={testId && `${testId}-price`}>
            {gbp(v.price)}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 border-y border-line py-4">
          <Spec icon={CalendarDays} label="Year" value={v.year} />
          <Spec icon={Gauge} label="Mileage" value={miles(v.mileage)} />
          <Spec icon={Fuel} label="Fuel" value={v.fuel} />
          <Spec icon={Cog} label="Gearbox" value={v.transmission} />
        </div>
        <Link to={path} className="btn btn-outline mt-auto w-full" data-testid={testId && `${testId}-view-button`}>
          View Vehicle <ArrowRight className="arrow" aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
};
